import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, EmptyState, SparkleIcon, TabBar } from '../components/ui';
import { ActivityItem } from '../components/activity/ActivityItem';
import { ChipRow } from '../components/activity/ChipRow';
import { DayGroup, groupItemsByDay } from '../components/activity/DayGroup';
import {
  DateRangeRow,
  periodToRange,
  type PeriodPreset,
} from '../components/activity/DateRangeRow';
import { TotalPnlCard } from '../components/activity/TotalPnlCard';
import { WhaleLoader } from '../components/WhaleLoader';
import { useInfiniteActivity } from '../hooks/useInfiniteActivity';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import type {
  ActivityBotKindFilter,
  ActivityResultFilter,
} from '../lib/types';

type Tab = 'trades' | 'wallet';
type WalletKind = 'all' | 'deposit' | 'withdrawal' | 'ref' | 'fee';
type ResultChoice = 'all' | ActivityResultFilter;
type PlatformChoice = 'all' | ActivityBotKindFilter;

interface DateState {
  period: PeriodPreset;
  range: { from?: string; to?: string };
}

const INITIAL_DATE: DateState = { period: 'all', range: {} };

function walletKindToParam(k: WalletKind): string | undefined {
  if (k === 'all') return undefined;
  return k;
}

export function Activity() {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<Tab>('trades');

  // Per-tab filter state — kept independent so switching tabs back
  // and forth doesn't reset what the user just picked.
  const [date, setDate] = useState<DateState>(INITIAL_DATE);
  const [platform, setPlatform] = useState<PlatformChoice>('all');
  const [result, setResult] = useState<ResultChoice>('all');
  const [walletKind, setWalletKind] = useState<WalletKind>('all');

  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  // ``periodToRange('7d')`` reads ``Date.now()`` each call, so a bare
  // call on every render produced a brand-new ISO ``from_ts`` (off by
  // ms). That fed into ``useInfiniteActivity``'s queryKey and made
  // React Query orphan every fetch before it landed — which on prod
  // looked like "7d / 30d filter doesn't load anything". Memoise so
  // the cutoff is stable until the user changes the period.
  const range = useMemo(
    () => periodToRange(date.period, date.range),
    [date.period, date.range.from, date.range.to],
  );
  const q = useInfiniteActivity(
    tab === 'trades'
      ? {
          type: 'trades',
          bot_kind: platform === 'all' ? undefined : platform,
          result: result === 'all' ? undefined : result,
          ...range,
        }
      : {
          type: 'wallet',
          kinds: walletKindToParam(walletKind),
          ...range,
        },
  );

  const items = useMemo(
    () => q.data?.pages.flatMap((p) => p.items) ?? [],
    [q.data],
  );
  const counts = q.data?.pages[0]?.counts;
  const filteredPnl = Number(q.data?.pages[0]?.filtered_pnl ?? 0);

  const dayGroups = useMemo(
    () => groupItemsByDay(items, locale),
    [items, locale],
  );

  const sentinelRef = useInfiniteScrollSentinel({
    onLoadMore: () => q.fetchNextPage(),
    enabled: !!q.hasNextPage && !q.isFetchingNextPage && !q.isLoading,
  });

  return (
    <div className="mx-auto max-w-app px-5 pt-6">
      <h1 className="pb-display text-[28px] leading-tight">
        {t('activity.title')}
      </h1>
      <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
        {t('activity.subtitle')}
      </p>

      <div className="mt-5">
        <TabBar
          value={tab}
          fullWidth
          size="lg"
          onChange={setTab}
          options={[
            { value: 'trades', label: t('activity.tab.trades') },
            { value: 'wallet', label: t('activity.tab.wallet') },
          ]}
        />
      </div>

      {tab === 'trades' ? (
        <>
          <div className="mt-4">
            <TotalPnlCard pnl={filteredPnl} />
          </div>
          <div className="mt-4 space-y-2.5">
            <DateRangeRow
              label={t('activity.filter_period')}
              period={date.period}
              range={date.range}
              onChange={(period, rng) => setDate({ period, range: rng })}
            />
            <ChipRow
              label={t('activity.filter_platform')}
              value={platform}
              onChange={setPlatform}
              options={[
                { value: 'all', label: t('activity.platform_all') },
                { value: 'polymarket', label: t('activity.platform_polymarket') },
                { value: 'hyperliquid', label: t('activity.platform_hyperliquid') },
              ]}
            />
            <ChipRow
              label={t('activity.filter_result')}
              value={result}
              onChange={setResult}
              options={[
                { value: 'all', label: t('activity.result.all') },
                { value: 'won', label: t('activity.result.won') },
                { value: 'lost', label: t('activity.result.lost') },
                { value: 'cancelled', label: t('activity.result.cancelled') },
              ]}
            />
          </div>
          {counts ? (
            <p className="mt-3 px-1 text-[12px] text-tg-hint">
              {t('activity.counts.summary', {
                total: counts.total,
                won: counts.trade_won,
                lost: counts.trade_lost,
                cancelled: counts.trade_canceled,
              })}
            </p>
          ) : null}
        </>
      ) : (
        <div className="mt-4 space-y-2.5">
          <DateRangeRow
            label={t('activity.filter_period')}
            period={date.period}
            range={date.range}
            onChange={(period, rng) => setDate({ period, range: rng })}
          />
          <ChipRow
            label={t('activity.filter_kind')}
            value={walletKind}
            onChange={setWalletKind}
            options={[
              { value: 'all', label: t('activity.wallet_kind.all') },
              { value: 'deposit', label: t('activity.wallet_kind.deposit') },
              { value: 'withdrawal', label: t('activity.wallet_kind.withdrawal') },
              { value: 'ref', label: t('activity.wallet_kind.ref') },
              { value: 'fee', label: t('activity.wallet_kind.fee') },
            ]}
          />
        </div>
      )}

      <div className="mt-5 space-y-5">
        {q.isLoading ? (
          <Card pad="lg" className="flex justify-center">
            <WhaleLoader />
          </Card>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<SparkleIcon size={22} />}
            title={t('activity.empty_title')}
            body={t('activity.empty_body')}
          />
        ) : (
          dayGroups.map((bucket, idx) => (
            <DayGroup
              key={bucket.key}
              bucket={bucket}
              // Items load newest→oldest in contiguous pages, so only the
              // LAST (oldest) loaded day can still be partial. Hide its
              // per-day PnL while more pages exist — otherwise it shows a
              // sum of just the loaded orders for that day. Every earlier
              // day is fully loaded, and once paging is done all show.
              hidePnl={
                tab === 'wallet' ||
                (idx === dayGroups.length - 1 && !!q.hasNextPage)
              }
            >
              {bucket.items.map((it) => (
                <li key={`${it.kind}:${it.id}`}>
                  <ActivityItem item={it} locale={locale} />
                </li>
              ))}
            </DayGroup>
          ))
        )}
      </div>

      {q.isFetchingNextPage ? (
        <div className="mt-3 flex justify-center">
          <WhaleLoader />
        </div>
      ) : null}

      <div ref={sentinelRef} className="h-px w-full" aria-hidden />

      {!q.hasNextPage && items.length > 0 ? (
        <p className="pb-caption mt-4 text-center">
          {t('activity.end_of_list')}
        </p>
      ) : null}
    </div>
  );
}
